import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { breedingAPI } from '../services/api';
import BreedingFoalNames from '../components/BreedingFoalNames';
import { formatDate } from '../utils/dateFormat';
import { calculateDaysInFoal, formatMonthsAndDays, addDaysToDate } from '../utils/breedingDates';
import { cardSx } from '../theme';
import { useNotify } from '../context/NotifyContext';
import { DetailPageSkeleton } from '../components/LoadingSkeletons';

// ── Status chip ──────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  bred:      'info',
  confirmed: 'primary',
  foaled:    'success',
  open:      'warning',
  aborted:   'error',
};

function StatusChip({ status }) {
  return (
    <Chip
      label={status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
      color={STATUS_COLOR[status] || 'default'}
      size="small"
    />
  );
}

// ── Label / value pair ───────────────────────────────────────────────────────

function Field({ label, children, secondary }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="body2" color="textSecondary">
        {label}
        {secondary && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            {secondary}
          </Typography>
        )}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {children ?? 'N/A'}
      </Typography>
    </Grid>
  );
}

function CurrencyField({ label, value }) {
  const display = value != null
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value)
    : 'N/A';
  return <Field label={label}>{display}</Field>;
}

function BoolField({ label, value, paidDate }) {
  if (value == null) return <Field label={label}>N/A</Field>;
  return (
    <Field label={label}>
      {value ? `Yes${paidDate ? ` — ${formatDate(paidDate)}` : ''}` : 'No'}
    </Field>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function BreedingDetailPage() {
  const notify = useNotify();
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success(`Copied ${label}`);
    });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await breedingAPI.getById(id);
      setRecord(data);
    } catch (err) {
      console.error('Failed to load breeding record', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <DetailPageSkeleton
        backLabel="Back to Breeding"
        onBack={() => navigate('/breeding')}
        label="Loading breeding record…"
      />
    );
  }

  if (!record) {
    return (
      <Box>
        <Typography variant="h5">Breeding record not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/breeding')}>
          Back to Breeding
        </Button>
      </Box>
    );
  }

  const mareName   = record.mare_registered_name   || record.mare_name   || `Mare #${record.mare_id}`;
  const stallionName = record.stallion_registered_name || record.stallion_name || `Stallion #${record.stallion_id}`;
  const foalName   = record.foal_name || null;

  const breedingDatesDisplay = Array.isArray(record.breeding_dates) && record.breeding_dates.length > 0
    ? record.breeding_dates.map(formatDate).join(', ')
    : formatDate(record.breeding_date);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/breeding')}
        sx={{ mb: 2 }}
      >
        Back to Breeding
      </Button>

      {/* ── Header ── */}
      <Paper sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" component="span">
                {mareName}
              </Typography>
              <Typography variant="h5" component="span" color="text.secondary">×</Typography>
              <Typography variant="h5" component="span">
                {stallionName}
              </Typography>
              <IconButton
                size="small"
                onClick={() => navigate('/breeding')}
                aria-label="edit breeding record"
                title="Edit in Breeding page"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {record.notes && (
                <Button variant="outlined" size="small" onClick={() => setNotesOpen(true)}>
                  View Notes
                </Button>
              )}
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              {record.breeding_method} · {record.breeding_season ?? '—'}
            </Typography>
          </Box>
          <StatusChip status={record.pregnancy_status} />
        </Box>

        {/* ── Horses ── */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
              Horses
            </Typography>
          </Grid>

          <Field label="Mare">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box component={RouterLink} to={`/horses/${record.mare_id}`}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {mareName} <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
              {record.mare_aqha_reg_number && (
                <Tooltip title={`AQHA #: ${record.mare_aqha_reg_number}`} arrow>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(record.mare_aqha_reg_number, record.mare_aqha_reg_number)}
                    sx={{ p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Field>

          <Field label="Stallion">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box component={RouterLink} to={`/horses/${record.stallion_id}`}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {stallionName} <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
              {record.stallion_aqha_reg_number && (
                <Tooltip title={`AQHA #: ${record.stallion_aqha_reg_number}`} arrow>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(record.stallion_aqha_reg_number, record.stallion_aqha_reg_number)}
                    sx={{ p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Field>

          {record.mare_owner_name && (
            <Field label="Mare Owner">{record.mare_owner_name}</Field>
          )}

          {foalName && record.foal_id && (
            <Field label="Foal">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box component={RouterLink} to={`/horses/${record.foal_id}`}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {foalName} <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Box>
                {record.foal_registered_name && (
                  <Tooltip title={`Copy registered name: ${record.foal_registered_name}`} arrow>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(record.foal_registered_name, record.foal_registered_name)}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Field>
          )}

          {/* ── Breeding dates ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
              Dates
            </Typography>
          </Grid>

          <Field label="Breeding Method">{record.breeding_method}</Field>
          <Field label="Breeding Season">{record.breeding_season}</Field>
          <Field label="Breeding Date(s)">{breedingDatesDisplay || 'N/A'}</Field>

          {(record.breeding_start_date || record.breeding_end_date) && (
            <>
              <Field label="Cover Start">{formatDate(record.breeding_start_date)}</Field>
              <Field label="Cover End">{formatDate(record.breeding_end_date)}</Field>
            </>
          )}

          <Field label="Due Date">{formatDate(record.due_date)}</Field>
          <Field label="Actual Foaling Date">{formatDate(record.actual_foaling_date)}</Field>
          <Field label="Days In Foal">
            {(() => {
              const days = calculateDaysInFoal(record);
              if (days === null) return null;
              const monthsDays = formatMonthsAndDays(days);
              return monthsDays ? `${days} (${monthsDays})` : days;
            })()}
          </Field>

          {/* ── Checks ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
              Pregnancy Checks
            </Typography>
          </Grid>

          <Field
            label="Confirmed In Foal"
            secondary={`Due ${formatDate(addDaysToDate(record.breeding_date, 14)) || 'N/A'}`}
          >
            {formatDate(record.confirmed_in_foal_date)}
          </Field>
          <Field
            label="Heartbeat Check"
            secondary={`Due ${formatDate(addDaysToDate(record.breeding_date, 28)) || 'N/A'}`}
          >
            {formatDate(record.heart_beat_check)}
          </Field>
          <Field
            label="45-Day Check"
            secondary={`Due ${formatDate(addDaysToDate(record.breeding_date, 45)) || 'N/A'}`}
          >
            {formatDate(record.forty_five_day_check)}
          </Field>
          <Field
            label="60-Day Check"
            secondary={`Due ${formatDate(addDaysToDate(record.breeding_date, 60)) || 'N/A'}`}
          >
            {formatDate(record.sixty_day_check)}
          </Field>
          {/* ── Stallion Breeding Reports ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
              Stallion Breeding Reports
            </Typography>
          </Grid>

          <Field label="AQHA SBR">{formatDate(record.aqha_sbr)}</Field>
          <Field label="APHA SBR">{formatDate(record.apha_sbr)}</Field>

          {/* ── Financials ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
              Financials
            </Typography>
          </Grid>

          <CurrencyField label="Stud Fee" value={record.stud_fee} />
          <BoolField label="Stud Fee Paid" value={record.stud_fee_paid} paidDate={record.stud_fee_paid_date} />
          <CurrencyField label="Booking Fee" value={record.booking_fee} />
          <BoolField label="Booking Fee Paid" value={record.booking_fee_paid} paidDate={record.booking_fee_paid_date} />
          <CurrencyField label="Mare Care Cost" value={record.mare_care_cost} />
          <CurrencyField label="Transport Cost" value={record.transport_cost} />

        </Grid>
      </Paper>

      <BreedingFoalNames breedingRecordId={record.id} />

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Notes</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {record.notes}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
